import React from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import {
    GetStockTransferQuery,
    useValidateStockTransfersMutation,
    StockTransferState,
    StockTransferFragmentFragment,
    TotadminPermission,
} from 'data/__generated__';

import { StatusTag } from 'pages/StockTransfers/components/StatusTag';

import { StateHistory } from 'components/DetailsView/StateHistory';
import { ColumnsSectionContainer, SectionColumn } from 'components/DetailsView/Section';
import { TotemPrimaryLoadingButton } from 'components/TotemPrimaryLoadingButton';
import { confirmPopup } from 'components/Popups/Confirm';
import { type GraphQLFormattedError } from 'graphql';

export const StatesSection = ({
    stateHistory,
    stockTransfer,
    isDirty,
}: {
    stateHistory: GetStockTransferQuery['stockTransfer']['stateHistory'];
    stockTransfer: GetStockTransferQuery['stockTransfer'];
    isDirty: boolean;
}) => {
    const { _id: stockTransferId, state } = stockTransfer;

    const [validateStockTransfers, { loading: validateStockTransferLoading }] = useValidateStockTransfersMutation();

    const STATE_CHANGE_ACTIONS = [
        {
            label: 'Valider le transfert de stock',
            dataTest: 'stock-transfer-validate',
            stateToSet: StockTransferState.Validated,
            isDisabled: state !== StockTransferState.Created,
        },
    ];
    const formattedStateHistory = stateHistory.map((stateRecord) => ({
        ...stateRecord,
        state: <StatusTag state={stateRecord.state} />,
    }));

    const handleStateUpdate = async (stateToSet: StockTransferState) => {
        if (stateToSet !== StockTransferState.Validated) return;

        if (
            stockTransfer.shouldUpdateStockImmediately &&
            !(await confirmPopup({
                title: 'Confirmer',
                message: 'Vous allez mettre à jour les stocks immédiatement sans livraison ni RE, ok pour vous ?',
            }))
        ) {
            return;
        }

        const { data: validationResponse, errors: graphqlErrors } = await validateStockTransfers({
            variables: { stockTransferIds: [stockTransferId] },
        });
        handleToast({
            graphqlErrors,
            errors: validationResponse?.validateStockTransfers?.errors || [],
            stockTransfer: validationResponse?.validateStockTransfers?.stockTransfers?.[0],
        });
    };

    function handleToast({
        graphqlErrors,
        errors,
        stockTransfer,
    }: {
        graphqlErrors?: readonly GraphQLFormattedError[] | null;
        errors: string[];
        stockTransfer: StockTransferFragmentFragment | undefined;
    }) {
        if (graphqlErrors && graphqlErrors.length) {
            toast.error('Une erreur est survenue lors de la validation du transfert de stock');
            return;
        }
        if (errors.length) {
            errors.forEach((error, index) => {
                toast.error(<span key={index}>Erreur : {error}</span>, { autoClose: false });
            });

            if (errors.length > 1) {
                toast.info('Cliquez pour fermer toutes les notifications', {
                    autoClose: false,
                    onClick: () => toast.dismiss(),
                });
            }
        }

        if (stockTransfer) {
            toast.success('Le statut du transfert de stock a été modifié !');
        }
    }

    return (
        <ColumnsSectionContainer numberOfColumns={3}>
            <StateHistory label="Historique de statuts" records={formattedStateHistory} />
            <StatusTag state={state} showHeading />
            <SectionColumn>
                {isDirty ? (
                    <WarningText>
                        ⚠️ Veuillez vous assurer d'avoir sauvegardé ou annulé vos ajustements avant de mettre à jour le
                        statut du ST
                    </WarningText>
                ) : null}
                <OverlaySectionColumn $isDirty={isDirty}>
                    {STATE_CHANGE_ACTIONS.map((stateChange, index) => {
                        const { label, stateToSet, isDisabled, dataTest } = stateChange;

                        async function updateState(e: React.MouseEvent<HTMLElement, MouseEvent>) {
                            e.preventDefault();
                            const hasConfirmed = await confirmPopup({
                                title: 'Confirmer',
                                message: `Êtes-vous sûr.e de vouloir ${label.toLowerCase()} ?`,
                            });
                            if (hasConfirmed) {
                                handleStateUpdate(stateToSet);
                            }
                        }

                        return (
                            <TotemPrimaryLoadingButton
                                key={index}
                                onClick={updateState}
                                $isSecondaryStyle={stateToSet !== StockTransferState.Validated}
                                disabled={isDisabled || isDirty}
                                loading={validateStockTransferLoading}
                                requiredPermissions={[TotadminPermission.StockTransfersManage]}
                                useIcon={false}
                                label={label}
                                data-test={dataTest}
                            />
                        );
                    })}
                </OverlaySectionColumn>
            </SectionColumn>
        </ColumnsSectionContainer>
    );
};

const WarningText = styled.div`
    color: ${({ theme }) => theme.textColor};
    font-weight: 800;
`;

const OverlaySectionColumn = styled(SectionColumn)<{ $isDirty: boolean }>`
    opacity: ${({ $isDirty }) => ($isDirty ? 0.4 : 1)};
`;
